import { DAY_NAMES, fmtDate, getWeekDays, statusBadge, getNewsTime } from '../calendarUtils';

function WeeklyView({
  currentWeekStart, newsMap, todayStr,
  rulesPanelOpen, isInRange, previewMap,
  openPostDetail, openDatePosts,
}) {
  const weekDays = getWeekDays(currentWeekStart);

  return (
    <>
      {/* 데스크탑: 7열 그리드 */}
      <div className="hidden sm:grid grid-cols-7 mb-2">
        {DAY_NAMES.map((name, i) => (
          <div key={name} className={`text-center py-2 font-bold text-xs ${i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : 'text-slate-500'}`}>
            {name}
          </div>
        ))}
      </div>

      {/* 데스크탑: 7열 레이아웃 */}
      <div className="hidden sm:grid grid-cols-7 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden"
           style={{ gap: '1px', backgroundColor: 'rgba(148,163,184,0.15)' }}>
        {weekDays.map((date, idx) => {
          const dateStr = fmtDate(date);
          const dayNews = newsMap[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSunday = idx === 0;
          const isSaturday = idx === 6;
          const maxDisplay = 5;
          const hasMore = dayNews.length > maxDisplay;
          const inRange = rulesPanelOpen && isInRange(dateStr);
          const previewCount = previewMap ? (previewMap[dateStr] || 0) : 0;

          return (
            <div key={idx} className={`min-h-[420px] p-2.5 bg-white dark:bg-slate-900 relative ${isToday ? 'ring-2 ring-primary ring-inset z-10' : ''} ${inRange ? 'bg-primary/5 dark:bg-primary/10' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-bold ${isToday ? 'text-primary' : isSunday ? 'text-red-500' : isSaturday ? 'text-blue-500' : 'dark:text-slate-300'}`}>
                  {date.getDate()}
                </span>
                {dayNews.length > 0 && (
                  <span className="text-[10px] text-slate-400 font-semibold">{dayNews.length}건</span>
                )}
              </div>

              {previewCount > 0 && (
                <div className="absolute top-2 right-2 bg-primary/20 text-primary text-[10px] font-bold px-1.5 py-0.5 rounded-full">+{previewCount}</div>
              )}

              <div className="space-y-2 overflow-hidden">
                {dayNews.slice(0, maxDisplay).map(news => {
                  const badge = statusBadge(news.uploadStatus);
                  const time = getNewsTime(news);
                  return (
                    <div key={news.id} onClick={() => openPostDetail(news)}
                      className={`bg-slate-100 dark:bg-slate-800/80 rounded-lg p-2 border ${badge.border} shadow-sm cursor-pointer hover:ring-1 hover:ring-primary/50 transition-all`}>
                      <div className="flex items-start gap-2">
                        {news.imageUrl && (
                          <div className="shrink-0 size-12 rounded overflow-hidden relative">
                            <img src={news.imageUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            {news.media?.length > 1 && (
                              <span className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/60 text-white text-[8px] font-bold rounded flex items-center gap-0.5">
                                <span className="material-symbols-outlined" style={{ fontSize: '9px' }}>photo_library</span>
                                {news.media.length}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] leading-tight dark:text-slate-300 line-clamp-2">{news.title || news.description || ''}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${badge.bg} ${badge.text}`}>{badge.label}</span>
                            {time && <span className="text-[9px] text-slate-400">{time}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {hasMore && (
                  <div onClick={() => openDatePosts(dateStr, dayNews)}
                    className="text-center text-[10px] text-primary font-bold cursor-pointer hover:underline py-1">
                    +{dayNews.length - maxDisplay} more
                  </div>
                )}

                {previewCount > 0 && (
                  <div className="text-center py-2 border-2 border-dashed border-primary/30 rounded bg-primary/5">
                    <span className="text-[10px] text-primary font-bold">{previewCount}개 생성 예정</span>
                  </div>
                )}
              </div>

              {dayNews.length === 0 && !previewCount && dateStr >= todayStr && (
                <div onClick={() => openDatePosts(dateStr, [])}
                  className="mt-2 text-center text-[11px] text-slate-400 hover:text-primary py-4 border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-primary/50 rounded-lg cursor-pointer transition-colors">
                  <span className="material-symbols-outlined text-base">add_circle_outline</span><br/>등록하기
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 모바일: 주간 리스트 뷰 */}
      <div className="sm:hidden space-y-2">
        {weekDays.map((date, idx) => {
          const dateStr = fmtDate(date);
          const dayNews = newsMap[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSunday = idx === 0;
          const isSaturday = idx === 6;
          const previewCount = previewMap ? (previewMap[dateStr] || 0) : 0;

          return (
            <div key={idx}
              onClick={() => openDatePosts(dateStr, dayNews)}
              className={`p-3 bg-white dark:bg-slate-900 rounded-xl border cursor-pointer transition-all ${isToday ? 'border-primary ring-1 ring-primary/30' : 'border-slate-200 dark:border-slate-800'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${isSunday ? 'text-red-500' : isSaturday ? 'text-blue-500' : 'text-slate-500'}`}>{DAY_NAMES[idx]}</span>
                  <span className={`text-base font-bold ${isToday ? 'text-primary' : 'dark:text-white'}`}>{date.getDate()}일</span>
                  {isToday && <span className="text-[10px] text-primary font-bold bg-primary/10 px-1.5 py-0.5 rounded-full">오늘</span>}
                </div>
                <div className="flex items-center gap-2">
                  {dayNews.length > 0 && (
                    <span className="text-xs font-semibold text-slate-500">{dayNews.length}건</span>
                  )}
                  {previewCount > 0 && (
                    <span className="text-[10px] text-primary font-bold">+{previewCount}</span>
                  )}
                  <span className="material-symbols-outlined text-slate-400 text-sm">chevron_right</span>
                </div>
              </div>
              {dayNews.length > 0 && (
                <div className="mt-2 flex gap-1.5 overflow-x-auto">
                  {dayNews.slice(0, 4).map(news => (
                    <div key={news.id} className="shrink-0 size-10 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      {news.imageUrl ? (
                        <img src={news.imageUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="material-symbols-outlined text-slate-400 text-sm">article</span>
                        </div>
                      )}
                    </div>
                  ))}
                  {dayNews.length > 4 && (
                    <div className="shrink-0 size-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-bold text-slate-500">+{dayNews.length - 4}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default WeeklyView;
